/* sd-stack.js — Senpai Drip AY-Line deck engine (idles when still)
   Also powers the product gallery card stack on phones (snippets/sd-product-gallery.liquid),
   which builds its own deck through window.SDStack.Deck. */
(() => {
  if (window.SDStack) { window.SDStack.init(); return; }

  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mod = (v, n) => ((v % n) + n) % n;
  const VISIBLE = 2.6; // cards drawn either side of centre — drop to 1.6 to paint fewer

  const rIC = window.requestIdleCallback ||
    ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 1));

  const sample = (url) => new Promise((res) => {
    if (!url) return res(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => res(null);
    img.onload = () => {
      try {
        const n = 24, c = document.createElement('canvas');
        c.width = c.height = n;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, n, n);
        const d = ctx.getImageData(0, 0, n, n).data;
        let br = 0, bg = 0, bb = 0, best = -1, fr = 0, fg = 0, fb = 0, cnt = 0;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          if (d[i + 3] < 128) continue;
          fr += r; fg += g; fb += b; cnt++;
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          const sat = mx === 0 ? 0 : (mx - mn) / mx;
          const lum = (mx + mn) / 510;
          const score = sat * (1 - Math.abs(lum - 0.55) * 1.4);
          if (score > best) { best = score; br = r; bg = g; bb = b; }
        }
        if (!cnt) return res(null);
        if (best < 0.12) { br = fr / cnt; bg = fg / cnt; bb = fb / cnt; }
        res(`rgb(${Math.round(br)},${Math.round(bg)},${Math.round(bb)})`);
      } catch (_) { res(null); }
    };
    img.src = url;
  });

  class Deck {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-stage]');
      this.cards = [...root.querySelectorAll('[data-card]')];
      this.n = this.cards.length;
      if (!this.stage || !this.n) return;

      this.dots = [...root.querySelectorAll('[data-dot]')];
      this.countEl = root.querySelector('[data-count]');
      this.prevBtn = root.querySelector('[data-prev]');
      this.nextBtn = root.querySelector('[data-next]');
      this.accents = this.cards.map((c) => c.dataset.accent || '');
      this.autoTint = root.dataset.tint === '1';
      this.tintQueued = this.cards.map(() => false);

      this.loop = this.n > 2;
      this.pos = 0; this.vel = 0; this.target = 0; this.active = -1; this.from = 0;
      this.spread = RM.matches ? 1 : 0; this.spreadTarget = this.spread;
      this.drag = null; this.suppress = false; this.wheelLock = 0;
      this.raf = 0; this.last = 0; this.visible = false; this.moving = false;
      this.autoMs = (parseFloat(root.dataset.autoplay) || 0) * 1000;
      this.autoStopped = this.autoMs <= 0 || this.n < 2;
      this.timer = 0;

      /* The deck rests after this many self-advances instead of looping for
         as long as the section is on screen. Someone who lingers stops having
         the card pulled away from them every few seconds. 0 = keep looping.
         A swipe, tap, arrow, dot or key still stops it for good via
         interact(). */
      const cap = parseInt(root.dataset.autoplayMax, 10);
      this.autoMax = Number.isFinite(cap) && cap >= 0 ? cap : 3;
      this.autoCount = 0;

      this.step = this.step.bind(this);
      this.measure();
      this.bind();
      this.render();
      this.setActive(0);
      root.classList.add('is-ready');

      /* Colour sampling is NOT started here — see tintNear(). It used to run
         at construction, i.e. DOMContentLoaded: downloading and canvas-
         sampling EVERY card image while the page was still loading, competing
         with the assets the user can actually see. It now waits until the
         section is on screen, and samples only the card in view plus its
         immediate neighbours, filling the rest in as the shopper moves.

         The sampled colour is the ONLY source of the backdrop wash — the
         blurred image bleed is gone, and .sd-stack__glow is a plain radial
         gradient reading --sk-accent. */
    }

    /* sample one card, once, on idle */
    tintAt(k) {
      if (!this.autoTint) return;
      if (k < 0 || k >= this.n) return;
      if (this.accents[k] || this.tintQueued[k]) return;
      this.tintQueued[k] = true;
      rIC(() => {
        sample(this.cards[k].dataset.sample).then((c) => {
          if (!c) return;
          this.accents[k] = c;
          if (k === this.active) this.root.style.setProperty('--sk-accent', c);
        });
      }, { timeout: 1200 });
    }

    /* the card on screen and the ones a swipe away — nothing further out */
    tintNear() {
      if (!this.autoTint || !this.visible) return;
      const i = this.active < 0 ? 0 : this.active;
      this.tintAt(i);
      this.tintAt(mod(i + 1, this.n));
      if (this.n > 2) this.tintAt(mod(i - 1, this.n));
    }

    measure() {
      const c = this.cards[0];
      this.W = c.offsetWidth || 280;
      this.H = c.offsetHeight || this.W * 1.6;
    }

    wrap(d) { return this.loop ? mod(d + this.n / 2, this.n) - this.n / 2 : d; }
    anchor() { if (Math.abs(this.pos - Math.round(this.pos)) < 0.02) this.from = mod(Math.round(this.pos), this.n); }

    /* promote to a GPU layer only while something is actually moving */
    setMoving(on) {
      if (this.moving === on) return;
      this.moving = on;
      this.cards.forEach((c) => c.classList.toggle('is-moving', on));
    }

    bind() {
      const s = this.stage;

      this.onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.anchor();
        this.setMoving(true);
        this.drag = { x: e.clientX, y: e.clientY, start: this.pos, moved: false, id: e.pointerId,
          lx: e.clientX, lt: e.timeStamp, v: 0, unit: this.W * 0.8 };
      };
      this.onMove = (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (!d.moved) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dy) > Math.abs(dx)) { this.drag = null; this.setMoving(false); return; }
          d.moved = true;
          this.interact();
          try { s.setPointerCapture(d.id); } catch (_) {}
          s.classList.add('is-dragging');
        }
        let p = d.start - dx / d.unit;
        if (!this.loop) {
          const max = this.n - 1;
          if (p < 0) p *= 0.3; else if (p > max) p = max + (p - max) * 0.3;
        }
        const dt = (e.timeStamp - d.lt) / 1000;
        if (dt > 0.001) {
          const inst = -(e.clientX - d.lx) / d.unit / dt;
          d.v = d.v * 0.5 + inst * 0.5; d.lx = e.clientX; d.lt = e.timeStamp;
        }
        this.pos = p;
        this.kick();
        if (e.cancelable) e.preventDefault();
      };
      this.onUp = (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        this.drag = null;
        if (!d.moved) { this.setMoving(false); return; }
        s.classList.remove('is-dragging');
        this.suppress = true; setTimeout(() => { this.suppress = false; }, 80);
        if (e.timeStamp - d.lt > 80) d.v = 0;
        const v = clamp(d.v, -8, 8);
        const base = Math.round(d.start);
        let t = clamp(Math.round(this.pos + v * 0.12), base - 2, base + 2);
        if (!this.loop) t = clamp(t, 0, this.n - 1);
        this.target = t;
        this.vel = RM.matches ? 0 : v;
        if (RM.matches) this.pos = t;
        this.kick();
      };
      this.onClick = (e) => {
        const el = e.target.closest('[data-card]');
        if (!el) return;
        if (this.suppress) { e.preventDefault(); e.stopPropagation(); return; }
        const i = +el.dataset.index;
        if (i !== this.active) { e.preventDefault(); this.interact(); this.goTo(i); }
      };
      this.onKey = (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.interact(); this.go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.interact(); this.go(-1); }
      };
      this.onWheel = (e) => {
        const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
        if (!dx || Math.abs(dx) < 4) return;
        e.preventDefault();
        if (e.timeStamp - this.wheelLock < 320) return;
        this.wheelLock = e.timeStamp;
        this.interact();
        this.go(dx > 0 ? 1 : -1);
      };
      this.onDragStart = (e) => e.preventDefault();
      this.onVis = () => { document.hidden ? clearTimeout(this.timer) : this.schedule(); };

      s.addEventListener('pointerdown', this.onDown);
      s.addEventListener('pointermove', this.onMove);
      s.addEventListener('pointerup', this.onUp);
      s.addEventListener('pointercancel', this.onUp);
      s.addEventListener('click', this.onClick, true);
      s.addEventListener('keydown', this.onKey);
      s.addEventListener('wheel', this.onWheel, { passive: false });
      s.addEventListener('dragstart', this.onDragStart);

      this.dots.forEach((b, i) => b.addEventListener('click', () => { this.interact(); this.goTo(i); }));
      this.prevBtn?.addEventListener('click', () => { this.interact(); this.go(-1); });
      this.nextBtn?.addEventListener('click', () => { this.interact(); this.go(1); });

      document.addEventListener('visibilitychange', this.onVis);

      /* WIDTH-ONLY resize.
         This fired on ANY size change — including the mobile address bar
         collapsing as you scroll, which changes viewport height and so
         restarted the whole rAF loop mid-scroll. Card width is the only
         thing measure() actually depends on. */
      this.ro = new ResizeObserver(() => {
        const w = this.cards[0].offsetWidth || 0;
        if (Math.abs(w - this.W) < 2) return;
        this.measure(); this.kick();
      });
      this.ro.observe(this.root);

      this.io = new IntersectionObserver(([en]) => {
        this.visible = en.isIntersecting;
        if (this.visible) {
          this.root.classList.add('is-in');
          this.spreadTarget = 1;
          if (RM.matches) this.spread = 1;
          this.kick(); this.schedule();
          this.tintNear();   // sample now, not at page load — and only nearby
        } else clearTimeout(this.timer);
      }, { threshold: 0.15 });
      this.io.observe(this.root);
    }

    kick() { if (!this.raf) { this.setMoving(true); this.raf = requestAnimationFrame(this.step); } }

    step(t) {
      this.raf = 0;
      const rm = RM.matches;
      const dt = Math.min(0.033, (t - (this.last || t)) / 1000) || 0.016;
      this.last = t;

      if (!this.drag) {
        if (rm) { this.pos = this.target; this.vel = 0; }
        else {
          const f = -165 * (this.pos - this.target) - 23 * this.vel;
          this.vel += f * dt; this.pos += this.vel * dt;
          if (Math.abs(this.pos - this.target) < 0.0008 && Math.abs(this.vel) < 0.002) {
            this.pos = this.target; this.vel = 0;
          }
        }
      }
      this.spread += (this.spreadTarget - this.spread) * (rm ? 1 : 1 - Math.exp(-dt * 4));
      if (Math.abs(this.spreadTarget - this.spread) < 0.0015) this.spread = this.spreadTarget;
      this.render();

      // the loop STOPS once everything has settled — nothing repaints while idle
      const settled = !this.drag && this.pos === this.target && this.spread === this.spreadTarget;
      if (!settled) this.raf = requestAnimationFrame(this.step);
      else { this.last = 0; this.setMoving(false); }
    }

    /* render() no longer writes --sd-a, --sd-g or --sk-mx. Those drove the
       per-card dim, the gloss sweep and the wordmark parallax, all removed.
       Each was a custom-property write per card per frame, and each forced a
       repaint of whatever read it. */
    render() {
      const { W, H, n } = this, s = this.spread;

      for (let i = 0; i < n; i++) {
        const c = this.cards[i];
        const d = this.wrap(i - this.pos), a = Math.abs(d);

        if (a > VISIBLE) { // fully skip far cards: no transform, no paint
          if (c.style.visibility !== 'hidden') { c.style.visibility = 'hidden'; c.style.opacity = '0'; }
          continue;
        }

        const sg = d < 0 ? -1 : 1, A = Math.min(a, 3);
        let x = sg * (Math.min(A, 1) * 0.30 + Math.max(0, A - 1) * 0.17) * W * s;
        let y = A * 9 * s;
        let rz = sg * A * 3.2 * s;
        let sc = 1 - A * 0.075 * s;

        if (i === this.from && a > 0 && a < 1) {
          const sub = Math.sqrt(Math.max(0, 1 - Math.abs((a - 0.5) / 0.5)));
          x += sg * W * sub * a;
          y -= H * 0.07 * sub * a;
          rz += sg * 20 * sub * a;
          sc -= 0.1 * sub;
        }

        const z = -A * 80 * s;
        const ry = clamp(d, -3, 3) * 9 * s;
        const op = a > VISIBLE - 0.6 ? clamp((VISIBLE - a) / 0.6, 0, 1) : 1;
        const lead = i === this.from && a < 0.5 ? 60 : 0;

        c.style.visibility = '';
        c.style.transform = `translate3d(calc(-50% + ${x.toFixed(1)}px),${y.toFixed(1)}px,${z.toFixed(0)}px) rotateY(${ry.toFixed(1)}deg) rotateZ(${rz.toFixed(1)}deg) scale(${sc.toFixed(3)})`;
        c.style.opacity = op.toFixed(2);
        c.style.zIndex = String(1000 - Math.round(a * 100) + lead);
      }

      const idx = mod(Math.round(this.pos), n);
      if (idx !== this.active) this.setActive(idx);
    }

    setActive(i) {
      const focused = this.cards.includes(document.activeElement);
      this.active = i;
      this.cards.forEach((c, k) => { c.classList.toggle('is-active', k === i); c.tabIndex = k === i ? 0 : -1; });
      if (focused) this.cards[i].focus({ preventScroll: true });
      this.dots.forEach((b, k) => b.setAttribute('aria-current', k === i ? 'true' : 'false'));
      if (this.countEl) this.countEl.textContent = `${String(i + 1).padStart(2, '0')} / ${String(this.n).padStart(2, '0')}`;
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = i === 0;
        if (this.nextBtn) this.nextBtn.disabled = i === this.n - 1;
      }
      if (this.accents[i]) this.root.style.setProperty('--sk-accent', this.accents[i]);
      this.tintNear();   // pull in the next neighbour as the shopper moves
      this.root.dispatchEvent(new CustomEvent('sd-stack:change', { detail: { index: i } }));
    }

    go(dir) {
      this.anchor();
      this.target = this.loop ? this.target + dir : clamp(this.target + dir, 0, this.n - 1);
      if (RM.matches) this.pos = this.target;
      this.kick();
    }

    goTo(i) {
      this.anchor();
      let d = i - mod(this.target, this.n);
      if (this.loop) d = this.wrap(d);
      this.target += d;
      if (RM.matches) this.pos = this.target;
      this.kick();
    }

    interact() { this.autoStopped = true; clearTimeout(this.timer); }

    schedule() {
      clearTimeout(this.timer);
      if (this.autoStopped || !this.visible || document.hidden) return;
      if (this.autoMax > 0 && this.autoCount >= this.autoMax) { this.autoStopped = true; return; }
      this.timer = setTimeout(() => {
        this.autoCount++;
        this.loop ? this.go(1) : this.goTo((mod(this.target, this.n) + 1) % this.n);
        this.schedule();
      }, this.autoMs);
    }

    destroy() {
      cancelAnimationFrame(this.raf); clearTimeout(this.timer);
      this.ro?.disconnect(); this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);

      /* the stage listeners used to survive a section unload — harmless on a
         live page where the node is discarded, but the theme editor unloads
         and reloads sections repeatedly */
      const s = this.stage;
      if (s) {
        s.removeEventListener('pointerdown', this.onDown);
        s.removeEventListener('pointermove', this.onMove);
        s.removeEventListener('pointerup', this.onUp);
        s.removeEventListener('pointercancel', this.onUp);
        s.removeEventListener('click', this.onClick, true);
        s.removeEventListener('keydown', this.onKey);
        s.removeEventListener('wheel', this.onWheel);
        s.removeEventListener('dragstart', this.onDragStart);
      }
    }
  }

  const init = (scope = document) =>
    scope.querySelectorAll('[data-sd-stack]').forEach((el) => { if (!el.__sdStack) el.__sdStack = new Deck(el); });

  /* Deck is shared so the product gallery can build its own stack on phones.
     Sections tagged [data-sd-stack] (Shop by Garment) are started by init() as before. */
  window.SDStack = { init, Deck };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) =>
    e.target.querySelectorAll('[data-sd-stack]').forEach((el) => { el.__sdStack?.destroy?.(); el.__sdStack = null; }));
  document.addEventListener('shopify:block:select', (e) => {
    const card = e.target.closest?.('[data-card]');
    const inst = card?.closest('[data-sd-stack]')?.__sdStack;
    if (inst) { inst.interact(); inst.goTo(+card.dataset.index); }
  });
})();